"use client";

import React from "react";
import { Check, Minus, Layers3 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const plans = [{
  id: "starter",
  name: "Starter",
  price: "$149/mo",
  cta: "Start Starter",
  link: "/PricingPage"
}, {
  id: "business",
  name: "Business",
  price: "$399/mo",
  cta: "Start Business",
  link: "/PricingPage",
  highlight: true
}, {
  id: "agency",
  name: "Agency",
  price: "$999/mo",
  cta: "Start Agency",
  link: "/PricingPage"
}, {
  id: "enterprise",
  name: "Enterprise",
  price: "From $2,499",
  cta: "Talk to Sales",
  link: "/BookADemoContactSalesPage"
}];
const matrixData = [{
  id: 1,
  isCategory: true,
  name: "Use Case Fit"
}, {
  id: 2,
  name: "Best for pilots and small teams",
  starter: true,
  business: false,
  agency: false,
  enterprise: false
}, {
  id: 3,
  name: "Best for growing teams and small agency operations",
  starter: false,
  business: true,
  agency: false,
  enterprise: false
}, {
  id: 4,
  name: "Best for reselling and high-volume usage",
  starter: false,
  business: false,
  agency: true,
  enterprise: false
}, {
  id: 5,
  name: "Best for tailored scale and custom requirements",
  starter: false,
  business: false,
  agency: false,
  enterprise: true
}, {
  id: 6,
  isCategory: true,
  name: "Plan Capacity"
}, {
  id: 7,
  name: "Tenants included",
  starter: "1",
  business: "3",
  agency: "10",
  enterprise: "Custom"
}, {
  id: 8,
  name: "Seats included",
  starter: "5",
  business: "20",
  agency: "50",
  enterprise: "Custom"
}, {
  id: 9,
  name: "Storage capacity",
  starter: "0.5GB",
  business: "2GB",
  agency: "10GB",
  enterprise: "Tier 4+"
}, {
  id: 10,
  name: "Vector capacity",
  starter: "100k",
  business: "250k",
  agency: "2.5M",
  enterprise: "Tier 4+"
}, {
  id: 11,
  name: "Queries / month",
  starter: "10k",
  business: "30k",
  agency: "300k",
  enterprise: "Tier 4+"
}, {
  id: 12,
  name: "Writes / month",
  starter: "30k",
  business: "100k",
  agency: "1M",
  enterprise: "Tier 4+"
}, {
  id: 13,
  isCategory: true,
  name: "RAG Control Layer"
}, {
  id: 14,
  name: "Draft → Review → Approved workflow",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 15,
  name: "Approved-only retrieval",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 16,
  name: "Citation-based answers",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 17,
  name: "Refusal when evidence is insufficient",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 18,
  name: "Audit log export",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 19,
  name: "Tenant isolation",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 20,
  name: "BYO-Key support",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 21,
  isCategory: true,
  name: "Agency and Scaling Fit"
}, {
  id: 22,
  name: "White-label eligible",
  starter: false,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 23,
  name: "Supports reseller workflows",
  starter: false,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 24,
  name: "High-volume tenant delivery",
  starter: false,
  business: false,
  agency: true,
  enterprise: true
}, {
  id: 25,
  name: "Dedicated deployment options",
  starter: false,
  business: false,
  agency: false,
  enterprise: true
}, {
  id: 26,
  isCategory: true,
  name: "Support and Commercial Model"
}, {
  id: 27,
  name: "Standard support",
  starter: true,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 28,
  name: "Priority support fit",
  starter: false,
  business: true,
  agency: true,
  enterprise: true
}, {
  id: 29,
  name: "Advanced onboarding",
  starter: false,
  business: false,
  agency: false,
  enterprise: true
}, {
  id: 30,
  name: "Sales-led plan design",
  starter: false,
  business: false,
  agency: false,
  enterprise: true
}];
function renderCellValue(value: boolean | string | undefined) {
  if (typeof value === "boolean") {
    return value ? <Check className="mx-auto size-5 text-primary" aria-label="Included" /> : <Minus className="mx-auto size-5 text-muted-foreground/50" aria-label="Not included" />;
  }
  return <span className="text-sm font-medium text-foreground">{value ?? "—"}</span>;
}
export default function PlatformCapabilitiesCompared() {
  return <section id="platform-capabilities-compared" className="bg-background py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            Plan Comparison
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Platform capabilities compared
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            Compare EloRAG plans by operational fit, RAG control model, deployment scale,
            and commercial readiness.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[0_12px_30px_-15px_rgba(0,0,0,0.12)]">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="sticky left-0 z-20 w-[30%] min-w-[240px] border-b border-border bg-background/95 p-5 align-bottom backdrop-blur-md">
                    <span className="text-lg font-semibold text-foreground">Capabilities</span>
                  </TableHead>

                  {plans.map((plan, i) => <TableHead key={plan.id} data-index={i} className={`min-w-[190px] border-b border-border bg-background/95 p-5 text-center align-bottom backdrop-blur-md ${plan.highlight ? "bg-primary/[0.03]" : ""}`}>
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-lg font-bold text-foreground">{plan.name}</span>
                        <span className="text-sm font-normal text-muted-foreground">{plan.price}</span>
                        <Button variant={plan.highlight ? "default" : "outline"} size="sm" className="mt-1 h-9 w-full max-w-[132px]" asChild>
                          <Link to={plan.link}>{plan.cta}</Link>
                        </Button>
                      </div>
                    </TableHead>)}
                </TableRow>
              </TableHeader>

              <TableBody>
                {matrixData.map((row, i) => {
                if (row.isCategory) {
                  return <TableRow key={row.id} data-index={i} className="border-y border-border bg-secondary/35 hover:bg-secondary/35">
                        <TableCell colSpan={5} className="p-4 text-sm font-semibold uppercase tracking-wider text-foreground">
                          {row.name}
                        </TableCell>
                      </TableRow>;
                }
                return <TableRow key={row.id} data-index={i} className="transition-colors hover:bg-muted/20">
                      <TableCell className="sticky left-0 bg-card p-4 font-medium text-foreground">
                        {row.name}
                      </TableCell>
                      <TableCell className="p-4 text-center">{renderCellValue((row as any).starter)}</TableCell>
                      <TableCell className="p-4 text-center">{renderCellValue((row as any).business)}</TableCell>
                      <TableCell className="p-4 text-center">{renderCellValue((row as any).agency)}</TableCell>
                      <TableCell className="p-4 text-center">{renderCellValue((row as any).enterprise)}</TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-border/70 bg-secondary/25 p-4 text-center">
          <p className="text-sm leading-relaxed text-muted-foreground">
            White-label is not included in core plans. White-label requires Business plan or higher,
            a White-Label subscription, and at least one Capacity Pack.
          </p>
        </div>
      </div>
    </section>;
}